import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Send2 } from "iconsax-react";
import { runtimeConfig } from "@/shared/config/runtime";
import {
  SUPPORT_LOCATIONS,
  buildErrorReport,
  matchSupportPage,
  sendErrorReport,
  supportErrorMessage,
} from "@/shared/support";
import { Button, Prompt } from "@/shared/ui";

const LOAD_ERROR = { name: "LoadError", message: "A page section failed to load its data", stack: "" };

interface Props {
  error?: Error | null;
  size?: "md" | "lg";
  className?: string;
  onFailure?: () => void;
}

interface PromptState {
  title: string;
  text: string;
  variant: "success" | "error";
}

export default function SendErrorReportButton({ error = null, size = "md", className, onFailure }: Props) {
  const { t, i18n } = useTranslation("ErrorBoundary");
  const [isSending, setIsSending] = useState(false);
  const [sentTicketId, setSentTicketId] = useState<number | null>(null);
  const [prompt, setPrompt] = useState<PromptState | null>(null);

  async function handleSendReport() {
    if (isSending || sentTicketId !== null) return;
    const pagePath = window.location.pathname;
    const routePattern = matchSupportPage(pagePath);
    const pageLabelKey = routePattern ? SUPPORT_LOCATIONS[routePattern].labelKey : null;
    const report = buildErrorReport(error, pagePath);
    setIsSending(true);
    try {
      await i18n.loadLanguages("ru");
      await i18n.loadNamespaces(["SupportPage", ...(pageLabelKey ? [pageLabelKey.split(":")[0]] : [])]);
      const tRu = i18n.getFixedT("ru");
      const ticketId = await sendErrorReport(runtimeConfig.apiUrl, error ? report : { ...report, error: LOAD_ERROR }, {
        routePattern,
        pageLabel: pageLabelKey ? tRu(pageLabelKey) : pagePath,
        spotLabel: tRu("SupportPage:form.spotOther"),
        locale: i18n.language,
      });
      setSentTicketId(ticketId);
      setPrompt({
        title: t("report.successTitle"),
        text: t("report.successText", { id: ticketId }),
        variant: "success",
      });
    } catch (err: unknown) {
      onFailure?.();
      setPrompt({
        title: t("report.errorTitle"),
        text: supportErrorMessage(err, i18n.getFixedT(null, "SupportPage")),
        variant: "error",
      });
    } finally {
      setIsSending(false);
    }
  }

  return (
    <>
      <Button
        variant="secondary"
        size={size}
        className={className}
        onClick={handleSendReport}
        disabled={isSending || sentTicketId !== null}>
        <Send2 size={16} color="currentColor" />
        {sentTicketId !== null
          ? t("report.sent", { id: sentTicketId })
          : isSending
            ? t("report.sending")
            : t("report.send")}
      </Button>
      {prompt && (
        <Prompt
          title={prompt.title}
          text={prompt.text}
          variant={prompt.variant}
          onClose={() => setPrompt(null)}
          namespace="ErrorBoundary"
        />
      )}
    </>
  );
}
